/**
 * Video Viewport Controller Module
 * Automatically pauses/plays autoplay videos based on viewport visibility
 * Uses Intersection Observer API for performance optimization
 */

/**
 * Configuration object for the video viewport controller
 */
const CONFIG = {
    // Threshold at which video should play/pause (30% = 0.3)
    visibilityThreshold: 0.3,
    
    // Root margin for intersection observer (can be adjusted for pre-loading)
    rootMargin: '0px',
    
    // Selector for autoplay videos
    videoSelector: 'video[autoplay]',
    
    // Selector for iframe videos (YouTube, Vimeo, etc.)
    iframeSelector: 'iframe[src*="youtube"], iframe[src*="vimeo"]',
    
    // Debug mode - set to true for console logging
    debug: false
};

/**
 * Log debug messages if debug mode is enabled
 * @param {string} message - The message to log
 * @param {*} data - Optional data to log with the message
 */
function debugLog(message, data = null) {
    if (CONFIG.debug) {
        console.log(`[Video Viewport Controller] ${message}`, data || '');
    }
}

/**
 * Check if a video was manually paused by the user
 * We track this to avoid auto-playing videos that users explicitly paused
 * @param {HTMLVideoElement} video - The video element
 * @returns {boolean} - True if manually paused
 */
function wasManuallyPaused(video) {
    return video.dataset.manuallyPaused === 'true';
}

/**
 * Mark a video as manually paused
 * @param {HTMLVideoElement} video - The video element
 * @param {boolean} isPaused - Whether the video is manually paused
 */
function setManuallyPaused(video, isPaused) {
    video.dataset.manuallyPaused = isPaused ? 'true' : 'false';
}

/**
 * Safely play a video element
 * Handles the promise returned by play() to avoid unhandled rejection errors
 * @param {HTMLVideoElement} video - The video element to play
 */
function safePlay(video) {
    // Don't play if user manually paused it
    if (wasManuallyPaused(video)) {
        debugLog('Video was manually paused, skipping autoplay', video.src);
        return;
    }
    
    const playPromise = video.play();
    
    if (playPromise !== undefined) {
        playPromise
            .then(() => {
                debugLog('Video started playing', video.src);
            })
            .catch(error => {
                // Handle autoplay restrictions (browser policy)
                debugLog('Video play failed (likely autoplay restriction)', error);
            });
    }
}

/**
 * Safely pause a video element
 * @param {HTMLVideoElement} video - The video element to pause
 */
function safePause(video) {
    if (!video.paused) {
        video.pause();
        debugLog('Video paused', video.src);
    }
}

/**
 * Handle iframe videos (YouTube, Vimeo)
 * Sends postMessage commands to control playback
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {boolean} shouldPlay - Whether to play or pause
 */
function handleIframeVideo(iframe, shouldPlay) {
    const src = iframe.src;
    
    // YouTube iframe API
    if (src.includes('youtube')) {
        const command = shouldPlay ? 'playVideo' : 'pauseVideo';
        iframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: command, args: '' }),
            '*'
        );
        debugLog(`YouTube ${shouldPlay ? 'play' : 'pause'} command sent`, src);
    }
    
    // Vimeo iframe API
    if (src.includes('vimeo')) {
        const command = shouldPlay ? 'play' : 'pause';
        iframe.contentWindow.postMessage(
            JSON.stringify({ method: command }),
            '*'
        );
        debugLog(`Vimeo ${shouldPlay ? 'play' : 'pause'} command sent`, src);
    }
}

/**
 * Create intersection observer callback
 * This is called whenever a video's visibility changes
 * @param {IntersectionObserverEntry[]} entries - Array of intersection entries
 */
function handleIntersection(entries) {
    entries.forEach(entry => {
        const element = entry.target;
        const isVisible = entry.intersectionRatio >= CONFIG.visibilityThreshold;
        
        debugLog('Video intersection changed', {
            element: element,
            ratio: entry.intersectionRatio,
            isVisible: isVisible
        });
        
        // Handle HTML5 video elements
        if (element.tagName === 'VIDEO') {
            if (isVisible) {
                safePlay(element);
            } else {
                safePause(element);
            }
        }
        
        // Handle iframe videos
        if (element.tagName === 'IFRAME') {
            handleIframeVideo(element, isVisible);
        }
    });
}

/**
 * Set up user interaction listeners to track manual pause/play
 * @param {HTMLVideoElement} video - The video element
 */
function setupUserInteractionListeners(video) {
    // Track when user manually pauses
    video.addEventListener('pause', () => {
        // Only mark as manually paused if the video was playing
        // and not paused by our script
        if (!video.paused) {
            setManuallyPaused(video, true);
        }
    });
    
    // Track when user manually plays
    video.addEventListener('play', () => {
        setManuallyPaused(video, false);
    });
}

/**
 * Initialize the video viewport controller
 * Finds all autoplay videos and sets up intersection observers
 */
export default function initVideoViewportController() {
    // Check if Intersection Observer is supported
    if (!('IntersectionObserver' in window)) {
        debugLog('IntersectionObserver not supported, video viewport control disabled');
        return;
    }
    
    // Create intersection observer
    const observer = new IntersectionObserver(handleIntersection, {
        root: null, // Use viewport as root
        rootMargin: CONFIG.rootMargin,
        threshold: [0, CONFIG.visibilityThreshold, 1] // Track multiple thresholds
    });
    
    // Find and observe all autoplay videos
    const videos = document.querySelectorAll(CONFIG.videoSelector);
    debugLog(`Found ${videos.length} autoplay video(s)`);
    
    videos.forEach(video => {
        // Initialize manually paused state
        setManuallyPaused(video, false);
        
        // Set up user interaction listeners
        setupUserInteractionListeners(video);
        
        // Start observing
        observer.observe(video);
        debugLog('Observing video', video.src);
    });
    
    // Find and observe iframe videos (YouTube, Vimeo)
    const iframes = document.querySelectorAll(CONFIG.iframeSelector);
    debugLog(`Found ${iframes.length} iframe video(s)`);
    
    iframes.forEach(iframe => {
        // Enable iframe API if not already enabled
        let src = iframe.src;
        
        // YouTube: Add enablejsapi parameter
        if (src.includes('youtube') && !src.includes('enablejsapi')) {
            src += (src.includes('?') ? '&' : '?') + 'enablejsapi=1';
            iframe.src = src;
        }
        
        // Vimeo: Add api parameter
        if (src.includes('vimeo') && !src.includes('api=1')) {
            src += (src.includes('?') ? '&' : '?') + 'api=1';
            iframe.src = src;
        }
        
        // Start observing
        observer.observe(iframe);
        debugLog('Observing iframe video', src);
    });
    
    // Store observer for potential cleanup
    if (videos.length > 0 || iframes.length > 0) {
        debugLog('Video viewport controller initialized successfully');
    } else {
        debugLog('No autoplay videos found on page');
    }
}

